import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { AuditLogRow } from 'src/app/core/services/audit-log-api.service';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { buildDiffRows, DiffRow, parseUserAgent } from './audit-diff.util';
import {
  actionLabel,
  actionVerbTone,
  outcomeChipClass,
  resourceLabel,
  TONE_CHIP_CLASS,
} from './audit-action-label.util';

interface AuditErrorInfo {
  status: number;
  message: string;
}

/**
 * Right-hand drawer for one audit entry. Leads with what changed (changed fields only),
 * then who/when/where, and folds the integrity hashes and raw metadata away.
 */
@Component({
  selector: 'app-audit-entry-drawer',
  standalone: true,
  imports: [CommonModule, IconModule],
  templateUrl: './audit-entry-drawer.component.html',
})
export class AuditEntryDrawerComponent {
  @Input() set row(value: AuditLogRow | null) {
    this._row = value;
    this.diff = value ? buildDiffRows(value.metadata) : [];
    this.ua = parseUserAgent(value?.user_agent ?? null);
    this.showAll = false;
    this.rawOpen = false;
    this.integrityOpen = false;
  }
  get row(): AuditLogRow | null {
    return this._row;
  }
  @Output() closed = new EventEmitter<void>();

  private _row: AuditLogRow | null = null;
  diff: DiffRow[] = [];
  ua = { browser: '—', platform: '—' };
  /** Changed fields only by default; the full snapshot is one click away. */
  showAll = false;
  rawOpen = false;
  integrityOpen = false;

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this._row) this.closed.emit();
  }

  get title(): string {
    return actionLabel(this._row?.action ?? '');
  }
  get chipClass(): string {
    return TONE_CHIP_CLASS[actionVerbTone(this._row?.action ?? '')];
  }
  get outcomeClass(): string {
    return outcomeChipClass(this._row?.outcome ?? 'success');
  }
  get record(): string {
    return resourceLabel(this._row?.resource_type ?? null, this._row?.resource_id ?? null);
  }
  get changed(): DiffRow[] {
    return this.diff.filter((d) => d.changed);
  }
  get visibleDiff(): DiffRow[] {
    return this.showAll ? this.diff : this.changed;
  }
  get unchangedCount(): number {
    return this.diff.length - this.changed.length;
  }
  /** Create/delete snapshots are one-sided, so "before"/"after" columns collapse to one. */
  get snapshotKind(): 'create' | 'delete' | 'update' | 'none' {
    const m = this._row?.metadata ?? {};
    const hasBefore = 'before' in m;
    const hasAfter = 'after' in m;
    if (hasBefore && hasAfter) return 'update';
    if (hasAfter) return 'create';
    if (hasBefore) return 'delete';
    return 'none';
  }
  get errorInfo(): AuditErrorInfo | null {
    return (this._row?.metadata?.['error'] as AuditErrorInfo | undefined) ?? null;
  }
  get rawMetadata(): string {
    return JSON.stringify(this._row?.metadata ?? {}, null, 2);
  }
  /** Strip JSON quotes from plain strings so values read as text, not code. */
  display(v: string): string {
    if (v === '') return '—';
    if (v === 'null') return 'empty';
    return v.startsWith('"') && v.endsWith('"') ? v.slice(1, -1) : v;
  }
}
